import type { QueryClient } from '@tanstack/react-query';
import { ServerStateKeysEnum } from '@/common/apis/serverStateKeysEnum';
import { getPaymentMethods } from '@/common/apis/services/factor/paymentMethods';
import { CENTERS } from '@/common/types/centers';
import { useUserInfoStore } from '@/modules/login/store/userInfo';
import { factorServicePriceKey, fetchFactorServicePrice } from './factorServicePrice';

const PRICE_STALE_MS = 30_000;

/**
 * Warms what the online-visit invoice needs before it can show its payment methods: the payable amount
 * (service-price, which adds the VAT) and then the payment methods for that amount. The katibe call takes
 * 1–2s, so starting it from the profile / search tap means the invoice finds both in the cache.
 * Keys and params are exactly the invoice's (useInvoice / Factor) — anything else would be a cache miss.
 */
export const prefetchFactorPayment = async (queryClient: QueryClient, serviceId: string, userCenterId: string) => {
  const { isLogin, info } = useUserInfoStore.getState();
  const centerId = CENTERS.CONSULT;

  const price = await queryClient
    .fetchQuery(factorServicePriceKey(centerId, serviceId, userCenterId, isLogin), () => fetchFactorServicePrice(centerId, serviceId, userCenterId), {
      staleTime: PRICE_STALE_MS,
      retry: 0,
    })
    .catch(() => null);
  const amount = price?.totalPrice;
  if (!amount) return;

  // Logged-in invoices also send the wallet balance; prefetch only when that balance is already known,
  // otherwise the params (and so the key) would differ from the invoice's.
  let balance: number | undefined;
  if (isLogin) {
    const cached = queryClient.getQueryData<any>([ServerStateKeysEnum.WalletBalance, info?.id]);
    balance = typeof cached?.data?.data?.balance === 'number' ? cached.data.data.balance : undefined;
    if (balance === undefined) return;
  }

  const timezone =
    typeof Intl?.DateTimeFormat?.()?.resolvedOptions()?.timeZone === 'string' ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined;
  const params = {
    amount,
    timezone,
    countryCode: info?.country_code_id,
    center_id: centerId,
    ...(balance !== undefined ? { balance } : {}),
  };
  await queryClient
    .prefetchQuery([ServerStateKeysEnum.PaymentMethods, params], () => getPaymentMethods(params), { staleTime: PRICE_STALE_MS, retry: 0 })
    .catch(() => undefined);
};
